import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, Link as LinkIcon, Save, Trash, RotateCcw, FileText, ExternalLink } from 'lucide-react';

const PolicyForm = ({ 
  policy = null, 
  onSubmit, 
  onCancel,
  isLoading = false,
  submitButtonText = 'Submit Policy',
  showDelete = false,
  onDelete = null,
  showAnalysisFields = false
}) => {
  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [sourceLink, setSourceLink] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState('');
  const [formError, setFormError] = useState(null);
  
  // Load existing policy data if provided
  useEffect(() => {
    if (policy) {
      setTitle(policy.title || '');
      setContent(policy.content || '');
      setSourceLink(policy.source_link || '');
      setDate(policy.date || new Date().toISOString().split('T')[0]);
      setSummary(policy.summary || '');
    }
  }, [policy]);
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);
    
    // Basic validation
    if (!title.trim()) {
      setFormError('Policy title is required');
      return;
    }
    
    if (!content.trim()) {
      setFormError('Policy content is required');
      return;
    }
    
    // Prepare data for submission
    const policyData = {
      title: title.trim(),
      content: content.trim(),
      source_link: sourceLink.trim(),
      date: date || new Date().toISOString().split('T')[0],
      summary: summary.trim()
    };
    
    // If editing an existing policy, include the ID
    if (policy && policy.id) {
      policyData.id = policy.id;
    }
    
    // Call the submit handler
    onSubmit(policyData);
  };
  
  // Handle reset form
  const handleReset = () => {
    if (policy) {
      // Reset to original policy data
      setTitle(policy.title || '');
      setContent(policy.content || '');
      setSourceLink(policy.source_link || '');
      setDate(policy.date || new Date().toISOString().split('T')[0]);
      setSummary(policy.summary || '');
    } else {
      // Clear form for new policy
      setTitle('');
      setContent('');
      setSourceLink('');
      setDate(new Date().toISOString().split('T')[0]);
      setSummary('');
    }
    setFormError(null);
  };
  
  // Handle delete confirmation
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this policy? This action cannot be undone.')) {
      onDelete && onDelete(policy.id);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error message */}
      {formError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{formError}</span>
          </div>
        </div>
      )}
      
      {/* Basic Info Section */}
      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mb-6">
        <h3 className="text-blue-800 font-medium mb-4">Basic Policy Information</h3>
        
        {/* Title */}
        <div className="mb-6">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Policy Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="Enter policy title"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Clear, descriptive title of the policy or government action
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Source Link */}
          <div>
            <label htmlFor="sourceLink" className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center">
                <ExternalLink className="h-4 w-4 mr-1" />
                Source Link
              </div>
            </label>
            <input
              type="url"
              id="sourceLink"
              name="sourceLink"
              value={sourceLink}
              onChange={(e) => setSourceLink(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://example.gov/policy-document"
            />
            <p className="mt-1 text-sm text-gray-500">
              Link to the original policy document
            </p>
          </div>
          
          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Publication Date
              </div>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              When the policy was announced
            </p>
          </div>
        </div>
      </div>
      
      {/* Content Section */}
      <div className="bg-green-50 p-4 rounded-md border border-green-200 mb-6">
        <h3 className="text-green-800 font-medium mb-4">Policy Content</h3>
        
        {/* Summary */}
        <div className="mb-6">
          <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-1">
            Policy Summary
          </label>
          <textarea
            id="summary"
            name="summary"
            rows={5}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief summary of the policy and its implications... (Use double line breaks for paragraphs)"
          />
          <p className="mt-1 text-sm text-gray-500">
            A concise summary explaining why this policy is important to analyze. Use double line breaks to create paragraph breaks.
          </p>
        </div>
        
        {/* Content */}
        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <FileText className="h-4 w-4 mr-1" />
              Full Policy Text *
            </div>
          </label>
          <textarea
            id="content"
            name="content"
            rows={15}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
            placeholder="Paste the full policy text here..."
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Full text of the policy document to be analyzed
          </p>
        </div>
      </div>
      
      {/* Form actions */}
      <div className="flex justify-between pt-5">
        <div className="flex">
          {/* Cancel button */}
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          
          {/* Reset button */}
          <button
            type="button"
            onClick={handleReset}
            className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </button>
          
          {/* Delete button (if editing) */}
          {showDelete && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="ml-3 inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Trash className="h-4 w-4 mr-1" />
              Delete
            </button>
          )}
        </div>
        
        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 ${
            isLoading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-1" />
              {submitButtonText}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default PolicyForm;